import { useRecoilValue } from 'recoil';
import { green, gold, blue, purple } from '@ant-design/colors';
import { Progress, Tooltip, Typography } from 'antd';
import {
  annotationMetricsTallyAtom,
  timeMetricsUIAtom,
} from 'packages/lib/metrics/state';
import { FC } from 'react';
import { devModeAtom } from 'packages/at-shell/state/recoil/ui';
import { showSessionTimerState } from 'packages/at-shell/state/recoil/ui';

const Hr: FC = () => <div className="hr" />;

const strokeColor = (
  active: number,
  inactive: number,
  total: number,
  steps: number
): string[] =>
  Array.from({ length: steps }, (_, i) => i).map((n) => {
    if (n <= steps * (active / total)) {
      return green[5] as string;
    }
    if (n <= steps * ((active + inactive) / total)) {
      return gold[5] as string;
    }
    return purple[3] as string;
  });

const Prog: FC = () => {
  const {
    activeTime: active,
    inactiveTime: inactive,
    elapsedClockTime: total,
  } = useRecoilValue(timeMetricsUIAtom);
  if (!(active + inactive) || !total) return null;
  const steps = 50;
  return (
    <Progress
      steps={steps}
      strokeColor={strokeColor(
        Math.round(active / 2),
        Math.round(inactive / 2),
        Math.round(total / 2),
        steps
      )}
      percent={100}
      showInfo={false}
      size="small"
      style={{ margin: '0.5rem 0' }}
    />
  );
};

const roundSec = (t: number): number => Math.round(t / 1000);

const SessionTimerInternal: FC = () => {
  const dev_mode = useRecoilValue(devModeAtom);
  const tally = useRecoilValue(annotationMetricsTallyAtom);
  const { activeTime, inactiveTime, elapsedClockTime } =
    useRecoilValue(timeMetricsUIAtom);
  const totalTrackedTime = activeTime + inactiveTime;
  const timeError = elapsedClockTime - totalTrackedTime;
  return (
    <div className="dev-mode-metrics-panel">
      <div className="inner">
        <Typography.Title
          style={{ margin: '0 0 0.5rem 0', lineHeight: '1rem' }}
          level={4}
        >
          Session Time
        </Typography.Title>
        <Hr />
        <Tooltip placement="right" title="Timer, for reference">
          <div>Elapsed clock time: {roundSec(elapsedClockTime)}s</div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Amount of active_time we've recorded in backend metrics API"
        >
          <div style={{ color: green[5] }}>
            Active time: {roundSec(activeTime)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Amount of inactive_time we've recorded in backend metrics API"
        >
          <div style={{ color: gold[5] }}>
            Non-interactive time: {roundSec(inactiveTime)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Difference between elapsed clock time and total recorded time, for reference"
        >
          <div style={{ color: purple[3] }}>
            Unracked Time: {timeError < 0 ? 0 : roundSec(timeError)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Total time we've recorded (active_time + inactive_time)"
        >
          <div style={{ color: blue[5], display: 'none' }}>
            Total tracked time: {roundSec(totalTrackedTime)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Visual indicator of ratio between active time, inactive time, and error in recorded time. Shows 50 steps, so 2% per step."
        >
          <div>
            <Prog />
          </div>
        </Tooltip>
      </div>
      {!dev_mode ? null : (
        <div className="inner">
          <Typography.Title
            style={{ margin: '0 0 0.25rem 0', lineHeight: '1rem' }}
            level={5}
          >
            Event Count:
          </Typography.Title>
          <Hr />
          {Object.entries(tally).map(([key, val]) => (
            <div key={`metrics-tally-${key}`}>
              <em>{key}:</em> <span>{val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SessionTimer: FC = () => {
  const dev_mode = useRecoilValue(devModeAtom);
  const showSessionTimer = useRecoilValue(showSessionTimerState);
  if (!dev_mode && !showSessionTimer) return null;
  return <SessionTimerInternal />;
};
